import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, CornerDownLeft, ScanBarcode, ShoppingCart, Trash2 } from "lucide-react";
import { BarcodeDialog } from "@/components/lists/BarcodeDialog";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, statusOf } from "@/lib/api";
import { useLive } from "@/lib/live";
import useListSync from "@/lib/useListSync";
import { useHub } from "@/components/AppShell";
import { Loader, NoAccess } from "@/components/common";
import ItemRow from "@/components/lists/ItemRow";
import ItemEditDialog from "@/components/lists/ItemEditDialog";
import RecipeDialog from "@/components/lists/RecipeDialog";
import ListForm from "@/components/lists/ListForm";
import { FollowButton, ListMenu, ShoppingBanner, SyncStatus, UsualChips } from "@/components/lists/ListParts";
import { AMBER, slug } from "@/components/lists/listsUi";
import { VoiceButton } from "@/components/voice/VoiceButton";

const SHOP_MS = 3 * 3600 * 1000;

function useWakeLock(on) {
  useEffect(() => {
    if (!on || !navigator.wakeLock) return undefined;
    let lock = null;
    const grab = () => navigator.wakeLock.request("screen").then((l) => { lock = l; }).catch(() => {});
    const vis = () => document.visibilityState === "visible" && grab();
    grab();
    document.addEventListener("visibilitychange", vis);
    return () => { document.removeEventListener("visibilitychange", vis); lock?.release().catch(() => {}); };
  }, [on]);
}

function Group({ title, testid, children, action }) {
  return (
    <section className="mb-5" data-testid={testid}>
      <div className="mb-1.5 flex items-center justify-between gap-2 px-1.5">
        <h2 className="text-[10px] font-black uppercase tracking-[0.18em] text-q-faint">{title}</h2>
        {action}
      </div>
      <ul className="q-card space-y-0.5 p-1.5 sm:p-2">{children}</ul>
    </section>
  );
}

export default function ListDetail() {
  const { hub } = useHub();
  const { listId } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { status } = useLive();
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(null);
  const [dups, setDups] = useState([]);
  const [recipe, setRecipe] = useState(false);
  const [barcode, setBarcode] = useState(false);
  const [manage, setManage] = useState(null);
  const [hl, setHl] = useState(new Set());
  const editingRef = useRef(null);
  editingRef.current = editing;
  const lastDup = useRef("");
  if (dups[0]) lastDup.current = dups[0];

  const onEvent = useCallback((m) => {
    if (m.type === "list.item.deleted" && editingRef.current && m.payload.item_ids.includes(editingRef.current.id)) {
      setEditing(null);
      toast(<span data-testid="item-removed-notice">{m.payload.by} removed this item</span>, { id: `removed-${m.payload.item_ids[0]}` });
    }
    if (m.type === "list.deleted") { toast(`${m.payload.by} deleted this list`); nav(`/h/${hub.id}/lists`); }
  }, [hub.id, nav]);
  const s = useListSync(hub.id, listId, onEvent);
  const d = s.data;
  const me = d?.me.key;
  const mine = d?.shoppers.find((x) => x.key === me);
  const shopping = !!mine;
  useWakeLock(shopping);

  const highlight = sp.get("highlight");
  useEffect(() => {
    const ids = new Set((highlight || "").split(",").filter(Boolean));
    setHl(ids);
    if (!ids.size) return undefined;
    const t = setTimeout(() => setHl(new Set()), 15000);
    return () => clearTimeout(t);
  }, [highlight]);
  const loaded = !!d;
  useEffect(() => {
    const first = [...hl][0];
    if (loaded && first) document.getElementById(`item-${first}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [hl, loaded]);
  useEffect(() => {
    if (!mine) return undefined;
    const t = setTimeout(s.reload, Math.max(1000, new Date(mine.started_at).getTime() + SHOP_MS - Date.now() + 500));
    return () => clearTimeout(t);
  }, [mine, s.reload]);

  if (s.error) return <NoAccess message={statusOf(s.error) === 404 ? "This list doesn't exist anymore" : errMsg(s.error)} action={<Link className="q-btn q-btn-secondary" to={`/h/${hub.id}/lists`} data-testid="list-back-link">Back to lists</Link>} />;
  if (!d) return <Loader />;

  const call = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); s.reload(); } catch (e) { toast.error(e.response ? errMsg(e) : "You're offline. Try again when you're back online."); } };
  const add = async (items, force = false) => {
    try {
      const r = await s.add(items, force);
      if (r.duplicates?.length) setDups((p) => [...p, ...r.duplicates]);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = () => {
    const parts = text.split(",").map((t) => t.trim()).filter(Boolean);
    if (!parts.length) return;
    if (parts.some((p) => p.length > 80)) { toast.error("Item names are 1–80 characters"); return; }
    setText("");
    add(parts.map((name) => ({ name })));
  };
  const onCheck = (item, v) => s.check(item, v).catch((e) => toast.error(errMsg(e)));
  const canEdit = (i) => d.can.edit_any || i.added_by?.key === me;

  const open = s.items.filter((i) => !i.checked);
  const cart = s.items.filter((i) => i.checked).sort((a, b) => (a.checked_at || "").localeCompare(b.checked_at || ""));
  const sorting = open.filter((i) => i.sorting || !i.aisle);
  const groups = d.aisles.map((a) => [a, open.filter((i) => !i.sorting && i.aisle === a)]).filter(([, v]) => v.length);
  const others = d.shoppers.filter((x) => x.key !== me);
  const row = (i) => <ItemRow key={i.id} item={i} shopping={shopping} canEdit={canEdit(i)} highlighted={hl.has(i.id)} onCheck={onCheck} onEdit={setEditing} />;

  return (
    <div className="mx-auto max-w-3xl" data-testid="list-detail-page" data-shopping={shopping ? "1" : "0"}>
      <Link to={`/h/${hub.id}/lists`} className="mb-4 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-q-muted hover:text-q-text q-onwall" data-testid="list-back-btn"><ArrowLeft className="h-4 w-4" /> All lists</Link>
      <div className="mb-5 flex items-start gap-3 q-rise">
        <span className="inline-flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl text-3xl" style={{ background: `rgba(${AMBER},0.14)`, boxShadow: `0 0 28px rgba(${AMBER},0.2)` }}>{d.list.emoji}</span>
        <div className="min-w-0 flex-1 q-onwall">
          <h1 className="break-words font-display text-3xl font-extrabold tracking-tight text-q-text sm:text-4xl" data-testid="list-title">{d.list.name}</h1>
          <p className="text-sm text-q-muted" data-testid="list-store">{d.list.store || "Any store"} · <span data-testid="list-left-count">{open.length}</span> left</p>
        </div>
        {d.can.manage && <ListMenu onEdit={() => setManage("edit")} onDelete={() => setManage("delete")} />}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {shopping ? (
          <button className="q-btn min-h-[48px] px-5 font-extrabold" style={{ background: `rgb(${AMBER})`, color: "#1a1204", boxShadow: `0 0 24px rgba(${AMBER},0.45)` }}
            onClick={() => call(() => api.post(`${s.base}/shopping`, { active: false }))} data-testid="shopping-done-btn"><ShoppingCart className="h-5 w-5" /> Done shopping</button>
        ) : (
          <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => call(() => api.post(`${s.base}/shopping`, { active: true }))} data-testid="shopping-start-btn"><ShoppingCart className="h-4 w-4" /> I'm at the store</button>
        )}
        <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setRecipe(true)} data-testid="recipe-open-btn"><BookOpen className="h-4 w-4" /> Add from recipe</button>
        <VoiceButton hubId={hub.id} screen="lists" listId={listId} />
        <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setBarcode(true)} data-testid="barcode-open-btn"><ScanBarcode className="h-4 w-4" /> Scan barcode</button>
        <FollowButton following={d.following} onToggle={() => call(() => api.post(`${s.base}/follow`, { following: !d.following }))} />
        <span className="ml-auto"><SyncStatus status={status} online={s.online} pending={s.queue.length} /></span>
      </div>
      {shopping && <p className="mb-4 rounded-2xl px-4 py-3 text-sm font-bold" style={{ background: `rgba(${AMBER},0.12)`, color: `rgb(${AMBER})` }} data-testid="shopping-mode">Shopping mode · you're at {d.list.store || "the store"}. Others get pinged when you add.</p>}
      <ShoppingBanner others={others} store={d.list.store} />

      <form className="relative mb-5" onSubmit={(e) => { e.preventDefault(); submit(); }} data-testid="quick-add-form">
        <input className="q-input min-h-[52px] pr-14 text-base" placeholder="Add items: milk, eggs, bread" value={text} onChange={(e) => setText(e.target.value)} enterKeyHint="done" data-testid="quick-add-input" />
        <button type="submit" className="absolute right-1.5 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-xl" style={{ background: `rgba(${AMBER},0.16)`, color: `rgb(${AMBER})` }} aria-label="Add" data-testid="quick-add-submit"><CornerDownLeft className="h-5 w-5" /></button>
      </form>
      <UsualChips usual={d.usual} onAdd={(name) => add([{ name }])} onDismiss={(name) => call(() => api.post(`${s.base}/usual/dismiss`, { name }))} />

      {sorting.length > 0 && <Group title="Just added" testid="aisle-group-sorting">{sorting.map(row)}</Group>}
      {groups.map(([a, items]) => <Group key={a} title={a} testid={`aisle-group-${slug(a)}`}>{items.map(row)}</Group>)}
      {open.length === 0 && <p className="mb-6 rounded-2xl border border-dashed border-q-line px-4 py-8 text-center text-sm text-q-muted" data-testid="list-empty">Nothing left to get. Add something above.</p>}
      {cart.length > 0 && (
        <Group title={`In the cart · ${cart.length}`} testid="aisle-group-cart"
          action={d.can.clear_cart && <button className="q-btn q-btn-ghost min-h-[44px] px-3 text-xs" onClick={() => call(() => api.post(`${s.base}/clear-cart`), "Cart cleared")} data-testid="clear-cart-btn"><Trash2 className="h-3.5 w-3.5" /> Clear cart</button>}>
          {cart.map(row)}
        </Group>
      )}

      {editing && <ItemEditDialog item={editing} base={s.base} aisles={d.aisles} canDelete={d.can.delete_items} onClose={() => setEditing(null)} onDone={() => { setEditing(null); s.reload(); }} />}
      {barcode && <BarcodeDialog hubId={hub.id} onClose={() => setBarcode(false)} onAdd={(it) => { add([it], true); toast.success(`Added ${it.name}`); }} />}
      {recipe && <RecipeDialog base={s.base} onClose={() => setRecipe(false)} onAdd={(items) => { setRecipe(false); add(items, true); toast.success(`Added ${items.length} item${items.length === 1 ? "" : "s"}`); }} />}
      {manage === "edit" && <ListForm hubId={hub.id} list={d.list} onClose={() => setManage(null)} onSaved={() => { setManage(null); s.reload(); }} />}

      <AlertDialog open={dups.length > 0} onOpenChange={(o) => !o && setDups([])}>
        <AlertDialogContent className="border-q-line bg-q-surface" data-testid="duplicate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="duplicate-message">{dups[0] || lastDup.current} is already on the list</AlertDialogTitle>
            <AlertDialogDescription className="text-q-muted">Add it again anyway?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDups((p) => p.slice(1))} data-testid="duplicate-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const n = dups[0]; setDups((p) => p.slice(1)); add([{ name: n }], true); }} data-testid="duplicate-add-anyway-btn">Add anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={manage === "delete"} onOpenChange={(o) => !o && setManage(null)}>
        <AlertDialogContent className="border-q-line bg-q-surface" data-testid="list-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display" data-testid="list-delete-message">Delete {d.list.name} and all its items?</AlertDialogTitle>
            <AlertDialogDescription className="text-q-muted">This removes it for everyone in the family.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="list-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 text-white hover:bg-red-500" data-testid="list-delete-confirm-btn"
              onClick={() => call(async () => { await api.delete(s.base); nav(`/h/${hub.id}/lists`); }, `${d.list.name} deleted`)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
